import { PlanField, PlanState, SelectedPlanField } from "../types"
import { createNewAction } from "./actions"
import { FieldAction, FieldObservation, ReferenceAction, SelectedFieldObservation } from "./types"

export function hasObservationChanged(a: FieldObservation, b: FieldObservation) {

    if (!(a && b)) {
        return false
    }

    function hasChanged<T extends { text: string }>(a: T, b: T, idSelector: (arg: T) => Number) {

        const id = (s: T) => idSelector(s) || 0
        const t = (s: T) => s.text || ""

        if (id(a) != id(b)) {
            return true
        }

        if (t(a) != t(b)) {
            return true
        }

    }

    if (hasChanged(a, b, s => s.observationId)) {
        return true
    }

    const aa = a.actions ?? []
    const ba = b.actions ?? []

    if (aa.length != ba.length) {
        return true
    }

    return aa.some((a, i) => hasChanged(a, ba[i], s => s.actionId))

}

export function updateObservation(state: PlanState, { key, text }: { key: number, text: string }) {

    const { selectedField, observations: reference } = state
    let { observations } = selectedField

    if (!observations) {
        return state
    }

    const observationId = Object.values(reference.observations).find(o => o.text === text)?.id

    observations = observations.map(obs => (obs.key !== key)
        ? obs
        : {
            ...obs,
            observationId,
            text,
            valid: Boolean(observationId || text)
        })


    return {
        ...state,
        selectedField: {
            ...selectedField,
            observations
        }
    }

}

export function deleteObservation(state: PlanState, { key, delete: deleted }: { key: number, delete: boolean }) {

    const { selectedField } = state
    let { observations } = selectedField

    if (!observations) {
        return state
    }

    observations = observations.map(obs => (obs.key !== key)
        ? obs
        : {
            ...obs,
            deleted
        })

    observations = observations.filter(o => !(o.new && o.deleted && isObservationEmpty(o)))

    return {
        ...state,
        selectedField: {
            ...selectedField,
            observations
        }
    }


}


function isObservationEmpty(obs: SelectedFieldObservation) {
    return !(obs.observationId || obs.text)
}

export function addNewObservation(state: PlanState): PlanState {

    const { selectedField } = state
    const { keyAllocator } = selectedField

    const key = keyAllocator()

    const observations = (selectedField.observations ?? []).concat({
        key,
        id: undefined,
        observationId: undefined,
        text: undefined,
        actions: [createNewAction(keyAllocator)],
        valid: false,
        updated: false,
        deleted: false,
        new: true
    })

    return {
        ...state,
        selectedField: {
            ...selectedField,
            activeObservation: { observation: key },
            observations
        }
    }

}

export function canAddNewObservation(field: SelectedPlanField) {

    if (!field?.valid) {
        return false
    }

    if (field.observations.some(o => !o.text)) {
        return false
    }

    return true

}

export function setupFieldObservations(
    field: PlanField["observations"],
    allocateKey: () => number,
    { observations, actions }: PlanState["observations"]): SelectedFieldObservation[] {


    function getObservationText(o: FieldObservation) {
        return o.observationId
            ? observations[o.observationId]?.text
            : undefined
    }

    function getActionText(a: FieldAction) {
        return a.actionId
            ? actions[a.actionId]?.text
            : undefined
    }

    const toRecord = <T extends { text: string }>(item: T, textProvider: (arg: T) => string) => ({
        ...item,
        text: textProvider(item) ?? item.text,
        key: allocateKey(),
        new: false,
        deleted: false,
        updated: false,
        valid: true
    })

    return (field ?? []).map(o => {

        const text = o.text ?? (
            o.observationId
                ? observations[o.observationId]?.text
                : undefined
        )

        const actions = o.actions ?? []

        if (actions.length === 0) {
            actions.push({ actionId: undefined, text: undefined })
        }

        return {
            ...toRecord(o, getObservationText),
            text,
            actions: actions.map(a => toRecord(a, getActionText)),
        }

    })

}

export type ObsActionsProvider = ReturnType<typeof getObservationActionsProvider>

export function getObservationActionsProvider({ observations, actions }: Pick<PlanState["observations"], "actions" | "observations">) {

    const cache = new Map<number, ReferenceAction[]>()

    return (obsId: number) => {

        if (!obsId) {
            return
        }

        if (cache.has(obsId)) {
            return cache.get(obsId)
        }

        const a = observations[obsId]?.actions

        const aa = a
            ? a.map(a => actions[a]).filter(a => !!a)
            : []

        cache.set(obsId, aa)

        return aa

    }

}
